import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AuditLogDto,
  SimulatedPersonDto,
  SimulatedPresenceDto,
} from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import SimulatedPersonModal from '../../components/modals/SimulatedPersonModal'
import {
  DEFAULT_SPAWN_LOCATION_PRESET,
  PersonMultiSelect,
  SPAWN_LOCATION_PRESETS,
  SpawnLocationPresetId,
  VenueOption,
  VenueSearchSelect,
} from '../../components/admin/simulation/SpawnSelectors'
import { adminService } from '../../services/admin.service'
import { useNavigate, useSearchParams } from 'react-router-dom'

function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56
  const headerHeight = 172
  const paginationHeight = 60

  const availableHeight = viewportHeight - headerHeight - paginationHeight
  const calculatedPageSize = Math.floor(availableHeight / rowHeight)
  return Math.max(10, Math.min(100, calculatedPageSize))
}

type RandomScopeMode = 'venues' | 'radius' | 'bbox'

const SimulationPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [entries, setEntries] = useState<SimulatedPersonDto[]>([])
  const [presence, setPresence] = useState<SimulatedPresenceDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [presenceMessage, setPresenceMessage] = useState('')
  const [presenceBusy, setPresenceBusy] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<SimulatedPersonDto | null>(null)

  const [spawnVenues, setSpawnVenues] = useState<VenueOption[]>([])
  const [spawnPersonPublicIds, setSpawnPersonPublicIds] = useState<string[]>([])
  const [spawnCount, setSpawnCount] = useState('3')

  const [randomCount, setRandomCount] = useState('5')
  const [randomMaxVenues, setRandomMaxVenues] = useState('5')
  const [randomScopeMode, setRandomScopeMode] = useState<RandomScopeMode>('radius')
  const [randomVenues, setRandomVenues] = useState<VenueOption[]>([])
  const [locationPresetId, setLocationPresetId] = useState<SpawnLocationPresetId>(
    DEFAULT_SPAWN_LOCATION_PRESET.id,
  )
  const [randomLatitude, setRandomLatitude] = useState(String(DEFAULT_SPAWN_LOCATION_PRESET.latitude))
  const [randomLongitude, setRandomLongitude] = useState(String(DEFAULT_SPAWN_LOCATION_PRESET.longitude))
  const [randomRadiusMeters, setRandomRadiusMeters] = useState(
    String(DEFAULT_SPAWN_LOCATION_PRESET.radiusMeters),
  )
  const [randomNeLat, setRandomNeLat] = useState(String(DEFAULT_SPAWN_LOCATION_PRESET.northeastLatitude))
  const [randomNeLng, setRandomNeLng] = useState(String(DEFAULT_SPAWN_LOCATION_PRESET.northeastLongitude))
  const [randomSwLat, setRandomSwLat] = useState(String(DEFAULT_SPAWN_LOCATION_PRESET.southwestLatitude))
  const [randomSwLng, setRandomSwLng] = useState(String(DEFAULT_SPAWN_LOCATION_PRESET.southwestLongitude))

  const [auditLogs, setAuditLogs] = useState<AuditLogDto[]>([])
  const [auditPagination, setAuditPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })

  const currentAuditPage = parseInt(searchParams.get('auditPage') || '1')
  const urlAuditPageSize = searchParams.get('auditPageSize')
  const currentAuditPageSize = urlAuditPageSize ? parseInt(urlAuditPageSize) : calculateOptimalPageSize()
  const currentAuditSortOrder = (searchParams.get('auditSortOrder') as 'asc' | 'desc') || 'desc'

  const activeLocationPreset = useMemo(
    () => SPAWN_LOCATION_PRESETS.find(preset => preset.id === locationPresetId) ?? DEFAULT_SPAWN_LOCATION_PRESET,
    [locationPresetId],
  )

  const applyLocationPreset = useCallback((presetId: SpawnLocationPresetId) => {
    const preset = SPAWN_LOCATION_PRESETS.find(item => item.id === presetId) ?? DEFAULT_SPAWN_LOCATION_PRESET
    setLocationPresetId(preset.id)
    setRandomLatitude(String(preset.latitude))
    setRandomLongitude(String(preset.longitude))
    setRandomRadiusMeters(String(preset.radiusMeters))
    setRandomNeLat(String(preset.northeastLatitude))
    setRandomNeLng(String(preset.northeastLongitude))
    setRandomSwLat(String(preset.southwestLatitude))
    setRandomSwLng(String(preset.southwestLongitude))
  }, [])

  const loadEntries = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await adminService.getSimulatedPersons()
      setEntries(response)
    } catch {
      setError('Failed to load simulated persons.')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadPresence = useCallback(async () => {
    try {
      const response = await adminService.getSimulatedPresence()
      setPresence(response)
    } catch (err) {
      console.log('Failed to fetch simulated presence', err)
    }
  }, [])

  const loadAuditLogs = useCallback(async () => {
    try {
      const response = await adminService.getSimulatedPersonAuditLogs(
        currentAuditPage,
        currentAuditPageSize,
        '',
        currentAuditSortOrder,
      )
      setAuditLogs(response.auditLogs)
      setAuditPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch simulated person audit logs', err)
    }
  }, [currentAuditPage, currentAuditPageSize, currentAuditSortOrder])

  useEffect(() => {
    loadEntries()
  }, [loadEntries])

  useEffect(() => {
    loadPresence()
  }, [loadPresence])

  useEffect(() => {
    loadAuditLogs()
  }, [loadAuditLogs])

  const handleEntrySaved = (saved: SimulatedPersonDto) => {
    setEntries(prev => {
      const exists = prev.some(entry => entry.publicId === saved.publicId)
      if (exists) {
        return prev.map(entry => (entry.publicId === saved.publicId ? saved : entry))
      }
      return [...prev, saved].sort((a, b) =>
        `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
      )
    })
    loadAuditLogs()
  }

  const handleEntryDeleted = (publicId: string) => {
    setEntries(prev => prev.filter(entry => entry.publicId !== publicId))
    setPresence(prev => prev.filter(row => row.person.publicId !== publicId))
    setSpawnPersonPublicIds(prev => prev.filter(id => id !== publicId))
    loadAuditLogs()
  }

  const handleAuditPageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('auditPage', page.toString())
    navigate(`?${newParams.toString()}`)
  }

  const handleAuditSortChange = (_sortBy: string, sortOrder: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('auditSortOrder', sortOrder)
    newParams.set('auditPage', '1')
    navigate(`?${newParams.toString()}`)
  }

  const runPresenceAction = async (action: () => Promise<string | void>) => {
    setPresenceBusy(true)
    setPresenceMessage('')
    setError('')
    try {
      const message = await action()
      await loadPresence()
      if (message) {
        setPresenceMessage(message)
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Presence action failed.'
      setError(typeof message === 'string' ? message : 'Presence action failed.')
    } finally {
      setPresenceBusy(false)
    }
  }

  const handleSpawnAtVenue = () =>
    runPresenceAction(async () => {
      const venuePublicId = spawnVenues[0]?.publicId
      if (!venuePublicId) {
        throw new Error('Select a venue first.')
      }
      const count = Number(spawnCount)
      const result = await adminService.spawnSimulatedPersonsAtVenue({
        venuePublicId,
        ...(spawnPersonPublicIds.length > 0
          ? { personPublicIds: spawnPersonPublicIds }
          : { count: Number.isFinite(count) ? count : 1 }),
      })
      return (
        `Spawned ${result.spawned.length} persona(s)` +
        (result.skippedPersonPublicIds.length ? `; skipped ${result.skippedPersonPublicIds.length}` : '')
      )
    })

  const handleSpawnRandom = () =>
    runPresenceAction(async () => {
      const count = Number(randomCount)
      const maxVenues = Number(randomMaxVenues)
      const payload: Parameters<typeof adminService.spawnSimulatedPersonsRandom>[0] = {
        count: Number.isFinite(count) ? count : 1,
        ...(Number.isFinite(maxVenues) && maxVenues > 0 ? { maxVenues } : {}),
      }

      if (randomScopeMode === 'venues') {
        if (randomVenues.length === 0) {
          throw new Error('Select at least one venue.')
        }
        payload.venuePublicIds = randomVenues.map(venue => venue.publicId)
      } else if (randomScopeMode === 'radius') {
        payload.latitude = Number(randomLatitude)
        payload.longitude = Number(randomLongitude)
        payload.radiusMeters = Number(randomRadiusMeters)
      } else {
        payload.northeastLatitude = Number(randomNeLat)
        payload.northeastLongitude = Number(randomNeLng)
        payload.southwestLatitude = Number(randomSwLat)
        payload.southwestLongitude = Number(randomSwLng)
      }

      const result = await adminService.spawnSimulatedPersonsRandom(payload)
      return `Randomly spawned ${result.spawned.length} persona(s) across venues.`
    })

  const handleClearAllPresence = () => {
    if (!window.confirm('Despawn all simulated people from the map?')) {
      return
    }
    runPresenceAction(async () => {
      const result = await adminService.clearAllSimulatedPresence()
      return `Cleared ${result.cleared} persona(s).`
    })
  }

  const handleDespawn = (personPublicId: string) =>
    runPresenceAction(async () => {
      await adminService.despawnSimulatedPerson(personPublicId)
      return 'Persona despawned.'
    })

  const tableRows = useMemo(
    () =>
      entries.map(entry => ({
        ...entry,
        fullName: `${entry.firstName} ${entry.lastName}`,
        preferredGendersLabel: entry.preferredGenders.join(', '),
        activeLabel: entry.isActive ? 'Active' : 'Inactive',
        vibeLabel: entry.vibeEmoji ?? '',
      })),
    [entries],
  )

  const presenceRows = useMemo(
    () =>
      presence.map(row => ({
        ...row,
        personPublicId: row.person.publicId,
        fullName: `${row.person.firstName} ${row.person.lastName}`,
        imageUrl: row.person.imageUrl,
        gender: row.person.gender,
      })),
    [presence],
  )

  const auditLogRows = useMemo(
    () =>
      auditLogs.map(log => ({
        ...log,
        data:
          log.data == null
            ? ''
            : typeof log.data === 'string'
              ? log.data
              : JSON.stringify(log.data),
      })),
    [auditLogs],
  )

  const idlePeople = useMemo(() => {
    const spawnedIds = new Set(presence.map(row => row.person.publicId))
    return entries.filter(entry => entry.isActive && !spawnedIds.has(entry.publicId))
  }, [entries, presence])

  if (loading) {
    return <></>
  }

  return (
    <div className="page simulation-page">
      <div className="page-header">
        <h1>Simulation</h1>
      </div>

      <p className="roles-page-intro">
        Catalog personas for investor demos. Spawn them onto the live map (in-memory) so they show in venues and accept
        one-way likes/messages. Restarting the API clears presence; the catalog stays.
      </p>

      {error && <div className="error-message">{error}</div>}
      {presenceMessage && <div className="simulation-status">{presenceMessage}</div>}

      <div className="simulation-presence-panel">
        <div className="users-header">
          <h2>Live presence ({presence.length})</h2>
          <div className="roles-actions">
            <button
              type="button"
              className="styled-button sm"
              disabled={presenceBusy || presence.length === 0}
              onClick={handleClearAllPresence}
            >
              <span>Clear all</span>
            </button>
            <button type="button" className="styled-button sm" disabled={presenceBusy} onClick={() => loadPresence()}>
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <p className="simulation-presence-meta">
          Idle active personas available to spawn: <strong>{idlePeople.length}</strong>
        </p>

        <div className="roles-table-container">
          <div className="table-wrapper">
            <Table
              id="simulated-presence"
              name="Simulated presence"
              keyField="personPublicId"
              initialSortColumn="checkedInAt"
              initialSortOrder="desc"
              columns={[
                { label: '', accessor: 'imageUrl', type: 'userAvatar' },
                { label: 'Name', accessor: 'fullName', type: 'string' },
                { label: 'Venue', accessor: 'venueName', type: 'string' },
                { label: 'Venue id', accessor: 'venuePublicId', type: 'string' },
                { label: 'Spawned', accessor: 'checkedInAt', type: 'datetime' },
                { label: 'Seconds', accessor: 'secondsInVenue', type: 'number' },
              ]}
              sourceData={presenceRows}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={(row: { personPublicId: string }) => {
                if (!presenceBusy) {
                  handleDespawn(row.personPublicId)
                }
              }}
            />
          </div>
        </div>
        <p className="simulation-hint">Click a presence row to despawn that persona.</p>

        <div className="simulation-spawn-grid">
          <section className="simulation-spawn-card">
            <h3>Spawn at venue</h3>
            <p>Pick a venue and optionally specific personas. Leave people empty to spawn a random idle batch.</p>

            <VenueSearchSelect
              label="Venue"
              hint="Search by venue name — not by public id."
              selected={spawnVenues}
              onChange={setSpawnVenues}
              disabled={presenceBusy}
            />

            <PersonMultiSelect
              label="Personas (optional)"
              hint="Only idle active catalog personas are listed."
              people={idlePeople}
              selectedPublicIds={spawnPersonPublicIds}
              onChange={setSpawnPersonPublicIds}
              disabled={presenceBusy}
            />

            {spawnPersonPublicIds.length === 0 && (
              <label className="form-group">
                <span>Count (random idle)</span>
                <p className="simulation-field-note">Used only when no personas are selected above.</p>
                <input value={spawnCount} onChange={e => setSpawnCount(e.target.value)} type="number" min={1} />
              </label>
            )}

            <button
              type="button"
              className="styled-button sm"
              disabled={presenceBusy || spawnVenues.length === 0}
              onClick={handleSpawnAtVenue}
            >
              <span>Spawn at venue</span>
            </button>
          </section>

          <section className="simulation-spawn-card">
            <h3>Spawn randomly by location</h3>
            <p>Distribute idle personas across open venues in a location scope.</p>

            <label className="form-group">
              <span>Count</span>
              <input value={randomCount} onChange={e => setRandomCount(e.target.value)} type="number" min={1} />
            </label>
            <label className="form-group">
              <span>Max venues</span>
              <input
                value={randomMaxVenues}
                onChange={e => setRandomMaxVenues(e.target.value)}
                type="number"
                min={1}
              />
            </label>

            <label className="form-group">
              <span>Location scope</span>
              <select
                value={randomScopeMode}
                onChange={e => setRandomScopeMode(e.target.value as RandomScopeMode)}
              >
                <option value="radius">Center + radius</option>
                <option value="bbox">Bounding box</option>
                <option value="venues">Selected venues</option>
              </select>
            </label>

            {randomScopeMode === 'venues' ? (
              <VenueSearchSelect
                label="Venues"
                hint="Search and add one or more open venues."
                multiple
                selected={randomVenues}
                onChange={setRandomVenues}
                disabled={presenceBusy}
              />
            ) : (
              <>
                <label className="form-group">
                  <span>Location preset</span>
                  <p className="simulation-field-note">{activeLocationPreset.note}</p>
                  <select
                    value={locationPresetId}
                    onChange={e => applyLocationPreset(e.target.value as SpawnLocationPresetId)}
                  >
                    {SPAWN_LOCATION_PRESETS.map(preset => (
                      <option key={preset.id} value={preset.id}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                </label>

                {randomScopeMode === 'radius' && (
                  <>
                    <label className="form-group">
                      <span>Latitude</span>
                      <p className="simulation-field-note">
                        {locationPresetId === 'custom'
                          ? 'Custom center latitude.'
                          : `${activeLocationPreset.label} center lat.`}
                      </p>
                      <input
                        value={randomLatitude}
                        onChange={e => {
                          setLocationPresetId('custom')
                          setRandomLatitude(e.target.value)
                        }}
                      />
                    </label>
                    <label className="form-group">
                      <span>Longitude</span>
                      <p className="simulation-field-note">
                        {locationPresetId === 'custom'
                          ? 'Custom center longitude.'
                          : `${activeLocationPreset.label} center lng.`}
                      </p>
                      <input
                        value={randomLongitude}
                        onChange={e => {
                          setLocationPresetId('custom')
                          setRandomLongitude(e.target.value)
                        }}
                      />
                    </label>
                    <label className="form-group">
                      <span>Radius (meters)</span>
                      <p className="simulation-field-note">Open venues inside this circle receive spawns.</p>
                      <input
                        value={randomRadiusMeters}
                        onChange={e => {
                          setLocationPresetId('custom')
                          setRandomRadiusMeters(e.target.value)
                        }}
                        type="number"
                        min={1}
                      />
                    </label>
                  </>
                )}

                {randomScopeMode === 'bbox' && (
                  <>
                    <label className="form-group">
                      <span>NE latitude</span>
                      <p className="simulation-field-note">Northeast corner of the box.</p>
                      <input
                        value={randomNeLat}
                        onChange={e => {
                          setLocationPresetId('custom')
                          setRandomNeLat(e.target.value)
                        }}
                      />
                    </label>
                    <label className="form-group">
                      <span>NE longitude</span>
                      <input
                        value={randomNeLng}
                        onChange={e => {
                          setLocationPresetId('custom')
                          setRandomNeLng(e.target.value)
                        }}
                      />
                    </label>
                    <label className="form-group">
                      <span>SW latitude</span>
                      <p className="simulation-field-note">Southwest corner of the box.</p>
                      <input
                        value={randomSwLat}
                        onChange={e => {
                          setLocationPresetId('custom')
                          setRandomSwLat(e.target.value)
                        }}
                      />
                    </label>
                    <label className="form-group">
                      <span>SW longitude</span>
                      <input
                        value={randomSwLng}
                        onChange={e => {
                          setLocationPresetId('custom')
                          setRandomSwLng(e.target.value)
                        }}
                      />
                    </label>
                  </>
                )}
              </>
            )}

            <button type="button" className="styled-button sm" disabled={presenceBusy} onClick={handleSpawnRandom}>
              <span>Spawn randomly</span>
            </button>
          </section>
        </div>
      </div>

      <div className="roles-actions" style={{ marginTop: '1.5rem' }}>
        <button type="button" onClick={() => setIsCreateModalOpen(true)} className="styled-button sm">
          <span>Add person</span>
        </button>
      </div>

      <div className="roles-table-container">
        <div className="table-wrapper">
          <Table
            id="simulated-persons"
            name="Simulated persons"
            keyField="publicId"
            initialSortColumn="fullName"
            initialSortOrder="asc"
            columns={[
              { label: '', accessor: 'imageUrl', type: 'userAvatar' },
              { label: 'Name', accessor: 'fullName', type: 'string' },
              { label: 'Gender', accessor: 'gender', type: 'enum', enumName: 'Gender' },
              { label: 'Prefers', accessor: 'preferredGendersLabel', type: 'string' },
              { label: 'Age', accessor: 'age', type: 'number' },
              { label: 'Vibe', accessor: 'vibeLabel', type: 'string' },
              { label: 'Status', accessor: 'activeLabel', type: 'string' },
              { label: 'Updated', accessor: 'updatedAt', type: 'datetime' },
            ]}
            sourceData={tableRows}
            isPropertyBarVisible={false}
            filterTerms=""
            onRowClick={(row: SimulatedPersonDto) =>
              setEditingEntry(entries.find(entry => entry.publicId === row.publicId) ?? null)
            }
          />
        </div>
      </div>

      <div className="users-header" style={{ marginTop: '2rem' }}>
        <h2>Change history</h2>
        <Pagination
          currentPage={auditPagination.page}
          totalPages={auditPagination.totalPages}
          onPageChange={handleAuditPageChange}
        />
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">
          <Table
            id="simulated-person-audit-logs"
            name="Simulated person audit logs"
            keyField="publicId"
            initialSortColumn="createdAt"
            initialSortOrder={currentAuditSortOrder}
            columns={[
              { label: 'Created', accessor: 'createdAt', type: 'datetime' },
              { label: 'Event', accessor: 'eventType', type: 'enum', enumName: 'AuditLogEventType' },
              { label: 'Performed by', accessor: 'performedByFullName', type: 'text' as any },
              { label: 'Comment', accessor: 'comment', type: 'text' as any },
              { label: 'Data', accessor: 'data', type: 'text' as any },
            ]}
            sourceData={auditLogRows}
            isPropertyBarVisible={false}
            filterTerms=""
            onSortChange={handleAuditSortChange}
            disableGlobalSearch={true}
            disableClientSort={true}
          />
        </div>
      </div>

      <SimulatedPersonModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        person={null}
        onSaved={handleEntrySaved}
      />

      <SimulatedPersonModal
        isOpen={editingEntry !== null}
        onClose={() => setEditingEntry(null)}
        person={editingEntry}
        onSaved={handleEntrySaved}
        onDeleted={handleEntryDeleted}
      />
    </div>
  )
}

export default SimulationPage
